import React, { useEffect, useState, useMemo } from 'react';
import { cn } from '../../lib/utils';
import { TrendingUp, BarChart3 } from 'lucide-react';

export function MonthlyTicketsChart({ data = [], periodLabel = 'Last 12 months' }) {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 200);
    return () => clearTimeout(timer);
  }, []);

  const chartData = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const hasNonZeroTickets = chartData.some((d) => (d.tickets || 0) > 0);
  const hasChartFrame = chartData.length > 0;

  // --- 1. DYNAMIC SCALING LOGIC (Fixes the "small graph" issue) ---
  const maxTickets = Math.max(...chartData.map((d) => d.tickets || 0), 0);
  const totalTickets = chartData.reduce((sum, d) => sum + (d.tickets || 0), 0);

  // Smart scale calculation: Adapts to small numbers (e.g., 4) or large numbers
  const getYAxisMax = (max) => {
    if (max === 0) return 5; // Default if empty
    if (max <= 5) return 5;  // If max is 4, scale is 5 (bars look tall)
    if (max <= 10) return 10;
    if (max <= 20) return 20;
    if (max <= 50) return 50;
    // For larger numbers, round up to nearest nice number
    const magnitude = Math.pow(10, Math.floor(Math.log10(max)));
    return Math.ceil(max / magnitude) * magnitude; 
  };

  const yAxisMax = getYAxisMax(maxTickets);
  const yAxisSteps = 5; // Keep 5 grid lines

  // --- 2. TREND CALCULATION ---
  const trend = useMemo(() => {
    if (chartData.length < 2) return { percentage: 0, isPositive: true };
    // Compare last month vs average of previous 3
    const currentMonth = chartData[chartData.length - 1]?.tickets || 0;
    const prevMonth = chartData[chartData.length - 2]?.tickets || 0;
    
    if (prevMonth === 0) return { percentage: currentMonth > 0 ? 100 : 0, isPositive: true };
    const diff = currentMonth - prevMonth;
    const percentage = Math.round((diff / prevMonth) * 100);
    
    return { percentage: Math.abs(percentage), isPositive: percentage >= 0 };
  }, [chartData]);

  return (
    <div 
      className={cn(
        "rounded-2xl bg-white dark:bg-slate-900 border border-gray-200/60 dark:border-slate-800 p-6 shadow-xl shadow-slate-200/40 dark:shadow-slate-900/40 h-full flex flex-col transition-all duration-700 ease-out",
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      )}
    >
      {/* Header Section */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="p-1.5 rounded-lg bg-indigo-50 dark:bg-indigo-500/10">
              <BarChart3 className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
            </div>
            <h3 className="text-base font-bold text-slate-800 dark:text-slate-100">
              Ticket Volume
            </h3>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 font-medium pl-1">
            {hasNonZeroTickets
              ? `Total ${totalTickets} tickets — ${periodLabel}`
              : hasChartFrame
                ? `${periodLabel} (no tickets in this window yet)`
                : 'No monthly data loaded'}
          </p>
        </div>

        {hasNonZeroTickets && (
          <div className={cn(
            "flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border",
            trend.isPositive 
              ? "bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20"
              : "bg-rose-50 text-rose-700 border-rose-100 dark:bg-rose-500/10 dark:text-rose-400 dark:border-rose-500/20"
          )}>
            <TrendingUp className={cn("w-3 h-3", !trend.isPositive && "rotate-180")} />
            <span>{trend.percentage}%</span>
            <span className="opacity-60 font-normal">vs last mo</span>
          </div>
        )}
      </div>

      {/* Chart Area — always render bars when we have 12 (or any) month slots so "last 12 months" is visible */}
      <div className="flex-1 relative min-h-[200px] w-full">
        {!hasChartFrame ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 dark:text-slate-600">
            <BarChart3 className="w-12 h-12 mb-2 opacity-20" />
            <p className="text-sm">No data available</p>
          </div>
        ) : (
          <>
            {/* Grid Lines & Y-Axis Labels */}
            <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
              {Array.from({ length: yAxisSteps + 1 }).map((_, i) => {
                const value = Math.round(yAxisMax - (i * (yAxisMax / yAxisSteps)));
                return (
                  <div key={i} className="flex items-center w-full h-0">
                    <span className="w-8 text-[10px] text-slate-400 dark:text-slate-600 text-right pr-3 -mt-2.5 font-medium tabular-nums">
                      {value}
                    </span>
                    <div className="flex-1 border-t border-dashed border-slate-100 dark:border-slate-800" />
                  </div>
                );
              })}
            </div>

            {/* Bars Container */}
            <div className="absolute inset-0 left-8 right-0 bottom-0 top-2 flex items-end justify-between gap-2 sm:gap-3 pl-2">
              {chartData.map((item, index) => {
                // Calculate height percentage relative to our dynamic max
                const rawPercent = yAxisMax > 0 ? (item.tickets / yAxisMax) * 100 : 0;
                const heightPercent = item.tickets > 0 ? Math.max(rawPercent, 2) : 0;
                const isHovered = hoveredIndex === index;
                const isMax = item.tickets === maxTickets && maxTickets > 0;

                return (
                  <div 
                    key={index}
                    className="relative flex-1 h-full flex flex-col justify-end group"
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                  >
                    {/* Floating Tooltip */}
                    <div className={cn(
                      "absolute bottom-[calc(100%+8px)] left-1/2 -translate-x-1/2 z-20 pointer-events-none transition-all duration-200 ease-out transform",
                      isHovered ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-2 scale-95"
                    )}>
                      <div className="bg-slate-900/90 text-white text-xs py-1.5 px-3 rounded-lg shadow-xl backdrop-blur-sm whitespace-nowrap border border-white/10">
                        <span className="font-bold">{item.tickets}</span>
                        <span className="opacity-70 ml-1.5 text-[10px]">in {item.month}</span>
                        {/* Little triangle arrow */}
                        <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-slate-900/90"></div>
                      </div>
                    </div>

                    {/* The Bar */}
                    <div className="relative w-full h-full flex items-end justify-center">
                      <div 
                        className={cn(
                          "w-full max-w-[24px] rounded-t-md transition-all duration-500 ease-out relative overflow-hidden",
                          isHovered 
                            ? "bg-indigo-500 shadow-[0_0_15px_-3px_rgba(99,102,241,0.6)]" 
                            : isMax 
                              ? "bg-indigo-500/90" 
                              : "bg-indigo-200/80 dark:bg-indigo-500/20",
                          item.tickets === 0 && "bg-slate-100 dark:bg-slate-800"
                        )}
                        style={{ height: `${heightPercent}%` }}
                      >
                        {/* Hover Gradient Overlay */}
                        <div className={cn(
                          "absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 transition-opacity duration-300",
                          isHovered && "opacity-100"
                        )} />
                      </div>
                    </div>

                    {/* X-Axis Label */}
                    <div className="h-6 flex items-center justify-center mt-2">
                      <span className={cn(
                        "text-[10px] sm:text-xs font-medium transition-colors duration-200 truncate px-0.5",
                        isHovered 
                          ? "text-indigo-600 dark:text-indigo-400 font-bold transform -translate-y-0.5" 
                          : "text-slate-400 dark:text-slate-500"
                      )}>
                        {item.month}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
